import { useLingui } from "@lingui/react/macro";
import { Box, CircularProgress, CssBaseline, ThemeProvider } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { createContext, use, useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router";
import ErrorPage from "@/components/ErrorPage";
import { config } from "@/config/env";
import { useIframeResize } from "@/hooks/useIframeResize";
import { api } from "@/services/api";
import { createWidgetTheme } from "@/theme/mui-theme";
import type { ThemeName } from "@/theme/presets";
import type { ThemeOverrides } from "@/theme/types";
import type { AffiliateBookingWidgetAppearance, CampgroundDataResponse } from "@/types/api";
import { analytics } from "@/utils/analytics";
import { getDateFnsLocale } from "@/utils/booking";
import { logError } from "@/utils/logError";

// TODO: remove once we deploy appearance config to prod
const DEMO_AFFILIATE_APPEARANCE: Record<string, AppearanceConfig> = {
  // bvcd-mv
  "c639008a-be0d-4e10-b5d4-24d775be700b": {
    theme: "light",
    primaryColor: "#207F98",
  },
  // Natur-Campingplatz Wilde-Heimat
  "1c600eb6-63ef-46df-86e6-135d68691437": {
    theme: "light",
    primaryColor: "#3C5D4C",
    backgroundColor: "#EFE9DF",
  },
  // Wohnmobilpark Am Hutrasen
  "d4d7a92a-1ea3-4323-9285-a085905bb42b": {
    theme: "light",
    primaryColor: "#bc0c6e",
  },
  // MainGlück Camping
  "8d1e534b-a9de-4015-886f-f45ab7ce7de4": {
    theme: "light",
    primaryColor: "#646A54",
  },
  // Schlaubetal Camping Schervenzsee GmbH
  "82cfbc3d-ab59-4ba1-9077-a22c6461f2aa": {
    theme: "light",
    primaryColor: "#DA8E36",
  },
  // Seeweide Naturcamping Penzlin
  "5d049703-65f1-44b9-b888-27b4dea623d0": {
    theme: "light",
    primaryColor: "#b5c349",
  },
  // Stellplatz Europe
  "c07e67db-f1fa-4782-ab79-aa3e31a30daf": {
    theme: "light",
    primaryColor: "#3E993E",
    affiliateLabel: "Your Booking is handled by CampNerd",
    affiliateLogoUrl:
      "https://cdn.prod.website-files.com/660d16e5e90ac8ecb4808189/66154537c07b29fb8d2b1ff9_sp-webclip%20copy.png",
  },
  // campio
  "7918fc69-a257-40b5-b351-66b334ee223d": {
    theme: "light",
    primaryColor: "#19988b",
    affiliateLabel: "Your Booking is handled by CampNerd",
    affiliateLogoUrl:
      "https://campio.no/_next/image?url=/_next/static/media/campio-logo-192.3f6b0c1b.png&w=48&q=75",
  },
  // Camperguru
  "c1dc7ede-e668-46c4-b4b2-877139cecfcc": {
    theme: "light",
    primaryColor: "#E3AE3F",
  },
  // CAMPING RESORT ZUGSPITZE
  "a0ecd27f-4afa-4092-bca2-e73b06a262da": {
    theme: "light",
    primaryColor: "#ED4458",
  },
  // CAMPING ERLEBNIS ZUGSPITZE
  "26c0afcc-ee56-460a-b559-f8375d797d77": {
    theme: "light",
    primaryColor: "#5D9D1C",
  },
  // Campingpark Rerik
  "e2cd6797-f87a-4b0a-9c2d-26cb42a9e644": {
    theme: "light",
    primaryColor: "#52706d",
  },
  // Waldcamping Am Großsee
  "640c8a07-c269-42c5-bbfd-5e0fbddbf70f": {
    theme: "light",
    primaryColor: "#8BA14C",
  },
};

// --- Types ---

type AppearanceConfig = {
  theme?: ThemeName;
  affiliateLogoUrl?: string;
  affiliateLabel?: string;
  imprintUrl?: string;
  privacyPolicyUrl?: string;
} & ThemeOverrides;

interface WidgetConfig {
  campgroundSlug?: string;
  aid?: string;
  language?: string;
  appearance?: AppearanceConfig;
}

interface AppContextValue {
  language: string;
  campgroundSlug: string;
  aid: string | null;
  theme: ReturnType<typeof createWidgetTheme>;
  affiliateLogoUrl: string | undefined;
  affiliateLabel: string | undefined;
  imprintUrl: string | undefined;
  privacyPolicyUrl: string | undefined;
  configReady: boolean;
  campgroundData: CampgroundDataResponse;
  currencyCode: string;
  setCurrencyCode: (code: string) => void;
}

// --- SessionStorage cache ---

const CAMPGROUND_CACHE_KEY = "campnerd_campground_data";
const CURRENCY_STORAGE_KEY = "campnerd_currency";

interface CachedCampgroundData {
  language: string;
  campgroundSlug: string;
  data: CampgroundDataResponse;
}

function getCachedCampgroundData(
  language: string,
  campgroundSlug: string,
): CampgroundDataResponse | null {
  const stored = sessionStorage.getItem(CAMPGROUND_CACHE_KEY);
  if (!stored) return null;
  try {
    const cached: CachedCampgroundData = JSON.parse(stored);
    if (cached.language === language && cached.campgroundSlug === campgroundSlug)
      return cached.data;
    return null;
  } catch (error) {
    logError(error);
    return null;
  }
}

function setCachedCampgroundData(
  language: string,
  campgroundSlug: string,
  data: CampgroundDataResponse,
) {
  try {
    sessionStorage.setItem(
      CAMPGROUND_CACHE_KEY,
      JSON.stringify({ language, campgroundSlug, data }),
    );
  } catch (error) {
    logError(error);
  }
}

function snakeToCamel(s: string): string {
  return s.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase());
}

function parseAffiliateBookingWidgetAppearance(
  raw: AffiliateBookingWidgetAppearance | string,
): AppearanceConfig {
  const t: AffiliateBookingWidgetAppearance = typeof raw === "string" ? JSON.parse(raw) : raw;
  return Object.fromEntries(
    Object.entries(t)
      .filter(([, v]) => Boolean(v))
      .map(([k, v]) => [snakeToCamel(k), v]),
  ) as AppearanceConfig;
}

// --- Context ---

const AppConfigContext = createContext<AppContextValue | undefined>(undefined);

export function AppConfigProvider({ children }: { children: React.ReactNode }) {
  const params = useParams<{ language: string; campground: string }>();
  const [searchParams] = useSearchParams();
  const { i18n, t } = useLingui();
  const isIframe = window.self !== window.top;

  const language = params.language!;
  const campgroundSlug = params.campground!;

  // --- Appearance ---
  const [localAppearance, setLocalAppearance] = useState<AppearanceConfig | undefined>();
  const [affiliateAppearance, setAffiliateAppearance] = useState<AppearanceConfig | undefined>();
  const [configReady, setConfigReady] = useState(!isIframe);
  const [aid, setAid] = useState<string | null>(searchParams.get("aid") ?? config.defaultAid);

  useEffect(() => {
    if (!isIframe) return;
    const handleMessage = (event: MessageEvent) => {
      if (event.data.type !== "campnerd.init") return;
      const widgetConfig: WidgetConfig = event.data.config;
      if (widgetConfig.appearance) setLocalAppearance(widgetConfig.appearance);
      if (widgetConfig.aid) setAid(widgetConfig.aid);
      setConfigReady(true);
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [isIframe]);

  useEffect(() => {
    analytics.setCampground(campgroundSlug);
  }, [campgroundSlug]);

  useEffect(() => {
    analytics.setAid(aid);
  }, [aid]);

  const mergedAppearance = useMemo<AppearanceConfig>(
    () =>
      ({
        ...affiliateAppearance,
        ...localAppearance,
        // TODO: remove once we deploy appearance config to prod
        ...(aid ? DEMO_AFFILIATE_APPEARANCE[aid] : {}),
      }) as AppearanceConfig,
    [affiliateAppearance, localAppearance, aid],
  );
  const { affiliateLogoUrl, affiliateLabel, imprintUrl, privacyPolicyUrl } = mergedAppearance;
  const theme = useMemo(() => {
    const {
      theme: themeName = "green",
      primaryColor,
      backgroundColor,
      borderRadius,
    } = mergedAppearance;
    const overrides: ThemeOverrides = {
      primaryColor,
      backgroundColor,
      borderRadius,
    };
    const hasOverrides = Object.values(overrides).some(Boolean);
    return createWidgetTheme(themeName as ThemeName, hasOverrides ? overrides : undefined);
  }, [mergedAppearance]);

  useIframeResize();

  // --- Campground Data ---
  const [campgroundData, setCampgroundData] = useState<CampgroundDataResponse | null>(() =>
    getCachedCampgroundData(language, campgroundSlug),
  );
  const [campgroundLoading, setCampgroundLoading] = useState(false);
  const [campgroundError, setCampgroundError] = useState<Error | null>(null);

  useEffect(() => {
    if (!campgroundSlug || !configReady) return;

    api.setCampground(campgroundSlug);
    api.setLanguage(language);
    api.setAid(aid);
    i18n.activate(language);

    const draftToken = new URLSearchParams(window.location.search).get("draftbooking");
    const cached = getCachedCampgroundData(language, campgroundSlug);
    if (cached && !draftToken) {
      setCampgroundData(cached);
      if (cached.affiliate_booking_widget_appearance)
        setAffiliateAppearance(
          parseAffiliateBookingWidgetAppearance(cached.affiliate_booking_widget_appearance),
        );
      return;
    }

    const fetchData = async () => {
      setCampgroundLoading(true);
      setCampgroundError(null);
      try {
        const fetchParams: Record<string, string> = draftToken ? { draftbooking: draftToken } : {};
        const response = await api.getCampgroundData(language, fetchParams);
        setCampgroundData(response);
        if (response.affiliate_booking_widget_appearance)
          setAffiliateAppearance(
            parseAffiliateBookingWidgetAppearance(response.affiliate_booking_widget_appearance),
          );
        const { draft_booking_data: _, ...dataToCache } = response;
        setCachedCampgroundData(language, campgroundSlug, dataToCache);
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        logError(error);
        setCampgroundError(error);
      } finally {
        setCampgroundLoading(false);
      }
    };

    fetchData();
  }, [language, campgroundSlug, i18n, aid, configReady]);

  // --- Currency ---
  const [currencyCode, setCurrencyCodeState] = useState<string>(
    () => localStorage.getItem(CURRENCY_STORAGE_KEY) || campgroundData?.campground?.currency || "",
  );

  useEffect(() => {
    if (campgroundData && !currencyCode && campgroundData.campground?.currency) {
      setCurrencyCodeState(campgroundData.campground.currency);
    }
  }, [campgroundData, currencyCode]);

  const setCurrencyCode = useCallback((code: string) => {
    setCurrencyCodeState(code);
    try {
      localStorage.setItem(CURRENCY_STORAGE_KEY, code);
    } catch (error) {
      logError(error);
    }
  }, []);

  // --- Locale  ---
  const dateFnsLocale = useMemo(() => getDateFnsLocale(i18n.locale), [i18n.locale]);

  const contextValue = useMemo<AppContextValue | undefined>(() => {
    if (!campgroundData) return undefined;
    return {
      language,
      campgroundSlug,
      aid,
      theme,
      affiliateLogoUrl,
      affiliateLabel,
      imprintUrl,
      privacyPolicyUrl,
      configReady,
      campgroundData,
      currencyCode,
      setCurrencyCode,
    };
  }, [
    language,
    campgroundSlug,
    aid,
    theme,
    affiliateLogoUrl,
    affiliateLabel,
    imprintUrl,
    privacyPolicyUrl,
    configReady,
    campgroundData,
    currencyCode,
    setCurrencyCode,
  ]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={dateFnsLocale}>
        {campgroundError ? (
          <ErrorPage message={t`This campground is not available for booking at the moment.`} />
        ) : !campgroundData || campgroundLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="100dvh">
            <CircularProgress sx={{ color: "black" }} />
          </Box>
        ) : (
          <AppConfigContext value={contextValue}>{children}</AppConfigContext>
        )}
      </LocalizationProvider>
    </ThemeProvider>
  );
}

// --- Hooks ---

function useAppContext(): AppContextValue {
  const ctx = use(AppConfigContext);
  if (!ctx) throw new Error("Must be used within AppConfigProvider");
  return ctx;
}

export function useAppConfig() {
  const {
    language,
    campgroundSlug,
    aid,
    theme,
    affiliateLogoUrl,
    affiliateLabel,
    imprintUrl,
    privacyPolicyUrl,
    configReady,
  } = useAppContext();
  return {
    language,
    campgroundSlug,
    aid,
    theme,
    affiliateLogoUrl,
    affiliateLabel,
    imprintUrl,
    privacyPolicyUrl,
    configReady,
  };
}

export function useCampground() {
  const { campgroundData } = useAppContext();
  return { data: campgroundData };
}

export function useCurrency() {
  const { currencyCode, setCurrencyCode, campgroundData } = useAppContext();
  return { currencyCode, setCurrencyCode, currencies: campgroundData.currencies };
}
